import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Gift, Plus, X, Check } from 'lucide-react';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GIFT_PACKAGES = [
  { id: '1_month', name: '1 Month', price: 20, pricePerMonth: 20 },
  { id: '6_months', name: '6 Months', price: 108, pricePerMonth: 18 },
  { id: '12_months', name: '12 Months', price: 192, pricePerMonth: 16 }
];

const GiftSubscriptionModal = ({ isOpen, onClose, user }) => {
  const [mode, setMode] = useState('send'); // 'send' or 'redeem'
  const [step, setStep] = useState('select'); // 'select', 'recipients', 'payment', 'success'
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [recipients, setRecipients] = useState(['']);
  const [processing, setProcessing] = useState(false);
  
  // Redeem state
  const [giftCode, setGiftCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  if (!isOpen) return null;

  const addRecipient = () => {
    setRecipients([...recipients, '']);
  };

  const removeRecipient = (index) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  const updateRecipient = (index, value) => {
    const newRecipients = [...recipients];
    newRecipients[index] = value;
    setRecipients(newRecipients);
  };

  const handlePurchase = async () => {
    // Validate emails
    const validEmails = recipients.filter(email => {
      const trimmed = email.trim();
      return trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    });

    if (validEmails.length === 0) {
      toast.error('Please enter at least one valid email address');
      return;
    }

    if (!selectedPackage) {
      toast.error('Please select a gift package');
      return;
    }

    try {
      setProcessing(true);
      
      const response = await axios.post(`${API}/gift-subscriptions/create`, {
        package_id: selectedPackage.id,
        recipient_emails: validEmails,
        gifter_name: user.name,
        gifter_email: user.email,
        origin_url: window.location.origin
      });

      if (response.data.success) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating gift subscription:', error);
      toast.error('Error processing gift subscription');
      setProcessing(false);
    }
  };

  const handleValidateCode = async () => {
    if (!giftCode.trim()) {
      toast.error('Please enter a gift code');
      return;
    }

    try {
      setValidating(true);
      const response = await axios.get(`${API}/gift-subscriptions/validate/${giftCode.trim()}`);
      setValidationResult(response.data);
      
      if (!response.data.valid) {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error validating code:', error);
      toast.error('Error validating gift code');
      setValidationResult({ valid: false, message: 'Invalid gift code' });
    } finally {
      setValidating(false);
    }
  };

  const handleRedeemCode = async () => {
    try {
      setProcessing(true);
      const response = await axios.post(`${API}/gift-subscriptions/redeem`, {
        gift_code: giftCode.trim(),
        user_id: user.id
      });

      if (response.data.success) {
        toast.success(response.data.message);
        onClose();
        // Refresh page to update subscription status
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error) {
      console.error('Error redeeming code:', error);
      toast.error(error.response?.data?.detail || 'Error redeeming gift code');
    } finally {
      setProcessing(false);
    }
  };

  const totalAmount = selectedPackage ? selectedPackage.price * recipients.filter(e => e.trim()).length : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-cream border-burgundy/20 max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-burgundy/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Gift className="h-6 w-6 text-burgundy" />
              <div>
                <CardTitle className="text-2xl font-serif text-burgundy">Gift Subscription</CardTitle>
                <CardDescription className="text-burgundy/70">
                  Send a gift or redeem a code
                </CardDescription>
              </div>
            </div>
            <button onClick={onClose} className="text-burgundy/50 hover:text-burgundy">
              <X className="h-6 w-6" />
            </button>
          </div>
          
          {/* Mode Toggle */}
          <div className="flex gap-2 mt-4">
            <Button
              onClick={() => setMode('send')}
              variant={mode === 'send' ? 'default' : 'outline'}
              className={mode === 'send' 
                ? 'flex-1 bg-burgundy text-white' 
                : 'flex-1 border-burgundy text-burgundy hover:bg-burgundy hover:text-white'
              }
              size="sm"
            >
              Send a Gift
            </Button>
            <Button
              onClick={() => setMode('redeem')}
              variant={mode === 'redeem' ? 'default' : 'outline'}
              className={mode === 'redeem' 
                ? 'flex-1 bg-burgundy text-white' 
                : 'flex-1 border-burgundy text-burgundy hover:bg-burgundy hover:text-white'
              }
              size="sm"
            >
              Redeem Code
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {mode === 'redeem' ? (
            // REDEEM MODE
            <>
              <div>
                <h3 className="font-semibold text-burgundy mb-3">Enter Your Gift Code</h3>
                <Input
                  type="text"
                  placeholder="Enter gift code"
                  value={giftCode}
                  onChange={(e) => {
                    setGiftCode(e.target.value);
                    setValidationResult(null);
                  }}
                  className="border-burgundy/30 focus:border-gold"
                />
              </div>

              {validationResult && (
                <div className={`rounded-lg p-4 border ${
                  validationResult.valid 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  {validationResult.valid ? (
                    <div>
                      <p className="font-semibold text-green-800">Valid Gift Code!</p>
                      <p className="text-sm text-green-700 mt-1">
                        Gift from: {validationResult.gifter_name}
                      </p>
                      <p className="text-sm text-green-700">
                        Duration: {validationResult.duration}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-red-700">{validationResult.message}</p>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleValidateCode}
                  disabled={!giftCode.trim() || validating}
                  variant="outline"
                  className="flex-1 border-burgundy text-burgundy hover:bg-burgundy/10"
                >
                  {validating ? 'Checking...' : 'Validate Code'}
                </Button>
                <Button
                  onClick={handleRedeemCode}
                  disabled={!validationResult?.valid || processing}
                  className="flex-1 bg-burgundy hover:bg-burgundy/80 text-white"
                >
                  {processing ? 'Redeeming...' : 'Redeem Gift'}
                </Button>
              </div>
            </>
          ) : (
            // SEND MODE
            <>
          {/* Step 1: Select Package */}
          <div>
            <h3 className="font-semibold text-burgundy mb-3">1. Choose a Gift Package</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {GIFT_PACKAGES.map(pkg => (
                <div
                  key={pkg.id}
                  onClick={() => setSelectedPackage(pkg)}
                  className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                    selectedPackage?.id === pkg.id
                      ? 'border-gold bg-gold/10'
                      : 'border-burgundy/20 hover:border-burgundy/40'
                  }`}
                >
                  {selectedPackage?.id === pkg.id && (
                    <div className="flex justify-end mb-2">
                      <Check className="h-5 w-5 text-gold" />
                    </div>
                  )}
                  <div className="text-center">
                    <p className="font-bold text-lg text-burgundy">{pkg.name}</p>
                    <p className="text-2xl font-bold text-burgundy mt-2">${pkg.price}</p>
                    <p className="text-xs text-burgundy/60 mt-1">${pkg.pricePerMonth}/month</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Step 2: Enter Recipients */}
          <div>
            <h3 className="font-semibold text-burgundy mb-3">2. Who are you gifting?</h3>
            <div className="space-y-3">
              {recipients.map((email, index) => (
                <div key={`recipient-${index}-${email}`} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="friend@example.com"
                    value={email}
                    onChange={(e) => updateRecipient(index, e.target.value)}
                    className="flex-1 border-burgundy/30 focus:border-gold"
                  />
                  {recipients.length > 1 && (
                    <Button
                      onClick={() => removeRecipient(index)}
                      variant="outline"
                      size="sm"
                      className="border-burgundy text-burgundy hover:bg-burgundy hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                onClick={addRecipient}
                variant="outline"
                size="sm"
                className="border-burgundy text-burgundy hover:bg-burgundy hover:text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Recipient
              </Button>
            </div>
          </div>

          {/* Summary */}
          {selectedPackage && (
            <div className="bg-burgundy/5 rounded-lg p-4 border border-burgundy/20">
              <h3 className="font-semibold text-burgundy mb-3">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-burgundy/70">Package:</span>
                  <span className="font-medium text-burgundy">{selectedPackage.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-burgundy/70">Price per gift:</span>
                  <span className="font-medium text-burgundy">${selectedPackage.price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-burgundy/70">Recipients:</span>
                  <span className="font-medium text-burgundy">{recipients.filter(e => e.trim()).length}</span>
                </div>
                <div className="border-t border-burgundy/20 pt-2 mt-2 flex justify-between">
                  <span className="font-semibold text-burgundy">Total:</span>
                  <span className="font-bold text-lg text-burgundy">${totalAmount}</span>
                </div>
              </div>
            </div>
          )}

          {/* Important Notes */}
          <div className="bg-gold/10 rounded-lg p-4 border border-gold/30">
            <p className="text-sm text-burgundy">
              <strong>Important:</strong> Gift codes expire 90 days after purchase. Recipients will receive an email with their unique gift code and redemption instructions.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-burgundy text-burgundy hover:bg-burgundy/10"
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={!selectedPackage || recipients.filter(e => e.trim()).length === 0 || processing}
              className="flex-1 bg-burgundy hover:bg-burgundy/80 text-white"
            >
              {processing ? 'Processing...' : `Continue to Payment ($${totalAmount})`}
            </Button>
          </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GiftSubscriptionModal;
